import React, { useCallback, useEffect, useMemo, useState } from "react";
import { literals } from "@/constants/literals";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import InputSelect from "@/components/input-select/InputSelect";
import InputText from "@/components/input-text/InputText";
import InputNumber from "@/components/input-number/InputNumber";
import Textarea from "@/components/textarea/Textarea";
import {
  CAPTURE_DESCRIPTION_OPTIONS,
  PASO_GARAGE_OPTIONS,
  PASO_NOSERV_OPTIONS,
  PASO_EXITS_OPTIONS,
  PUESTO_OPTIONS,
  PACKAGING_STEP_DESCRIPTION_OPTIONS,
} from "@/constants/selectsLists";
import {
  getNumberPhotosInGroup,
  changeGroupMetadata,
  getNumberPhotosInOrder,
  updateOrder,
  getAllStepsNumberPhotos,
} from "@/lib/services/images";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/store/useLoader";
import ConfirmEditionDetails from "./ConfirmEditionDetails";
import NacelleFinfingsInputSearchSelect from "@/components/input-search-select/NacelleFinfingsInputSearchSelect";
import { NACELLE_CONFIGURATION } from "@/constants/constants";
import { formatDateDDMmmYYYY } from "@/lib/dates";
import { formatBooleanText } from "@/lib/texts.utils";

export interface EditionPhotoDetailsProps {
  photoData: any;
  closeForm: () => void;
  refetch: () => void;
  backToTable: () => void;
}

const YES_NO_OPTIONS = [
  { label: literals.selectOption ?? "", value: "" },
  { label: literals.yes, value: "YES" },
  { label: literals.no, value: "NO" },
];

const toYesNo = (v: boolean | null | undefined) =>
  v === true ? "YES" : v === false ? "NO" : "";

const fromYesNo = (v: string): boolean | null =>
  v === "YES" ? true : v === "NO" ? false : null;

const EditionPhotoDetails = ({
  photoData,
  closeForm,
  refetch,
  backToTable,
}: EditionPhotoDetailsProps) => {
  const { upLoader, downLoader } = useLoader();

  const [description, setDescription] = useState("");
  const [descriptionType, setDescriptionType] = useState("");
  const [extraInformation, setExtraInformation] = useState("");
  const [isVisible, setIsvisible] = useState(false);

  const [customerOrderNoserv, setCustomerOrderNoserv] = useState("");

  const [orderIn, setOrderIn] = useState("");
  const [serialIn, setSerialIn] = useState("");

  const [sapWorkOrder, setSapWorkOrder] = useState("");
  const [document9r1, setDocument9r1] = useState("");

  const [orderOut, setOrderOut] = useState("");
  const [serialOut, setSerialOut] = useState("");

  const [customer, setCustomer] = useState("");

  const [ownPackaging, setOwnPackaging] = useState("");
  const [correctPackaging, setCorrectPackaging] = useState("");
  const [damagedPackaging, setDamagedPackaging] = useState("");
  const [packagingReference, setPackagingReference] = useState("");
  const [ownPackagingOut, setOwnPackagingOut] = useState("");
  const [iberiaPackagingOut, setIberiaPackagingOut] = useState("");

  const [confirmCount, setConfirmCount] = useState(0);

  const allStepsChange =
    (serialIn || "") !== (photoData?.serialIn || "") ||
    (serialOut || "") !== (photoData?.serialOut || "") ||
    (orderIn || "") !== (photoData?.orderIn || "") ||
    (orderOut || "") !== (photoData?.orderOut || "");

  const groupChanged =
    description.trim() !== (photoData.description ?? "").trim() ||
    isVisible !== photoData.visible;

  const orderChanged =
    (customerOrderNoserv ?? "") !== (photoData?.customerOrderNoserv ?? "") ||
    (customer ?? "") !== (photoData?.customer ?? "") ||
    (sapWorkOrder ?? "") !== (photoData?.sapWorkOrder ?? "") ||
    (document9r1 ?? "") !== (photoData?.document9r1 ?? "") ||
    (packagingReference ?? "") !== (photoData?.packagingReference ?? "") ||
    (ownPackaging ?? "") !== toYesNo(photoData?.ownPackaging) ||
    (correctPackaging ?? "") !== toYesNo(photoData?.correctPackaging) ||
    (damagedPackaging ?? "") !== toYesNo(photoData?.damagedPackaging) ||
    (ownPackagingOut ?? "") !== toYesNo(photoData?.ownPackagingOut) ||
    (iberiaPackagingOut ?? "") !== toYesNo(photoData?.iberiaPackagingOut);

  const [openModal, setOpenModal] = useState(false);

  const isNacelleFindings = useMemo(
    () =>
      photoData?.step === NACELLE_CONFIGURATION.FINDINGS &&
      photoData.area === NACELLE_CONFIGURATION.GARAGE_ID,
    [photoData],
  );

  useEffect(() => {
    setDescription(photoData?.description ?? "");
    if (
      (photoData?.step === literals.inNoServ ||
        photoData?.step === literals.packagingAction) &&
      (photoData?.description?.length ?? 0) > 0
    ) {
      const firstColom = photoData.description.indexOf(":");
      if (firstColom === -1) {
        setDescriptionType(photoData.description);
        setExtraInformation("");
      } else {
        setDescriptionType(photoData.description.slice(0, firstColom));
        setExtraInformation(photoData.description.slice(firstColom + 1).trim());
      }
    } else {
      setDescriptionType("");
      setExtraInformation("");
    }
    setIsvisible(!!photoData?.visible);

    setCustomerOrderNoserv(photoData?.customerOrderNoserv ?? "");

    setOrderIn(photoData?.orderIn ?? "");
    setSerialIn(photoData?.serialIn ?? "");

    setSapWorkOrder(photoData?.sapWorkOrder ?? "");
    setDocument9r1(photoData?.document9r1 ?? "");

    setOrderOut(photoData?.orderOut ?? "");
    setSerialOut(photoData?.serialOut ?? "");

    setCustomer(photoData?.customer ?? "");

    setOwnPackaging(toYesNo(photoData?.ownPackaging));
    setCorrectPackaging(toYesNo(photoData?.correctPackaging));
    setDamagedPackaging(toYesNo(photoData?.damagedPackaging));
    setPackagingReference(photoData?.packagingReference ?? "");
    setOwnPackagingOut(toYesNo(photoData?.ownPackagingOut));
    setIberiaPackagingOut(toYesNo(photoData?.iberiaPackagingOut));
  }, [photoData]);

  const changeOnltexDescription = useCallback((newDescription: string) => {
    setDescription(newDescription);
  }, []);

  const changeDescriptionType = useCallback(
    (selectedDescription: string) => {
      setDescriptionType(selectedDescription);
      if (extraInformation.trim().length > 0) {
        setDescription(`${selectedDescription}: ${extraInformation}`);
      } else {
        setDescription(selectedDescription);
      }
    },
    [extraInformation],
  );

  const changeExtraInformation = useCallback(
    (newExtraInfo: string) => {
      setExtraInformation(newExtraInfo);
      if (newExtraInfo.trim().length > 0) {
        setDescription(`${descriptionType}: ${newExtraInfo}`);
      } else {
        setDescription(descriptionType);
      }
    },
    [descriptionType],
  );

  const onChangeSapWorkOrder = useCallback((v: string) => {
    setSapWorkOrder(v);
    setPackagingReference("");
  }, []);

  const onChangeCustomer = useCallback((v: string) => {
    setCustomer(v);
    setPackagingReference("");
  }, []);

  const onChangeOwnPackaging = useCallback((v: string) => {
    setOwnPackaging(v);
    setPackagingReference("");
  }, []);

  const canNotDelete9r1 = useMemo(() => {
    return photoData?.document9r1?.length > 0 && document9r1.trim() === "";
  }, [photoData?.document9r1, document9r1]);

  const validation = useMemo(() => {
    const trimmedDescription = description.trim();

    const sapWorkOrderValid =
      sapWorkOrder === "" || /^\d{10,12}$/.test(sapWorkOrder);

    const customerValid = customer === "" || /^\d{3,6}$/.test(customer);

    const document9r1Valid = document9r1 === "" || /^\d{10}$/.test(document9r1);

    const descriptionValid =
      !isNacelleFindings || trimmedDescription.length > 0;

    const inNoServDescriptionValid =
      photoData?.step !== literals.inNoServ ||
      (descriptionType.trim().length > 0 &&
        (descriptionType !== "Others" || extraInformation.trim().length > 0));

    return {
      sapWorkOrderValid,
      customerValid,
      document9r1Valid,
      descriptionValid,
      inNoServDescriptionValid,
      isFormValid:
        sapWorkOrderValid &&
        customerValid &&
        document9r1Valid &&
        descriptionValid &&
        inNoServDescriptionValid &&
        !canNotDelete9r1,
    };
  }, [
    sapWorkOrder,
    customer,
    document9r1,
    description,
    descriptionType,
    extraInformation,
    isNacelleFindings,
    photoData?.step,
    canNotDelete9r1,
  ]);

  const canNotSend = useMemo(() => {
    if (!validation.isFormValid) return true;

    if (isNacelleFindings && description.length <= 0) return true;

    const unchangedDescriptionVisible =
      description.trim() === (photoData?.description ?? "").trim() &&
      isVisible === !!photoData?.visible;

    const unchangedDetails =
      customerOrderNoserv === (photoData?.customerOrderNoserv ?? "") &&
      orderIn === (photoData?.orderIn ?? "") &&
      serialIn === (photoData?.serialIn ?? "") &&
      sapWorkOrder === (photoData?.sapWorkOrder ?? "") &&
      document9r1 === (photoData?.document9r1 ?? "") &&
      orderOut === (photoData?.orderOut ?? "") &&
      serialOut === (photoData?.serialOut ?? "") &&
      customer === (photoData?.customer ?? "") &&
      ownPackaging === toYesNo(photoData?.ownPackaging) &&
      correctPackaging === toYesNo(photoData?.correctPackaging) &&
      damagedPackaging === toYesNo(photoData?.damagedPackaging) &&
      packagingReference === (photoData?.packagingReference ?? "") &&
      ownPackagingOut === toYesNo(photoData?.ownPackagingOut) &&
      iberiaPackagingOut === toYesNo(photoData?.iberiaPackagingOut);

    return (
      (unchangedDescriptionVisible && unchangedDetails) ||
      description === "Others" ||
      ((photoData?.step === literals.inNoServ ||
        photoData?.step === literals.packagingAction) &&
        !descriptionType)
    );
  }, [
    correctPackaging,
    customer,
    customerOrderNoserv,
    damagedPackaging,
    description,
    descriptionType,
    document9r1,
    iberiaPackagingOut,
    isNacelleFindings,
    isVisible,
    orderIn,
    orderOut,
    ownPackaging,
    ownPackagingOut,
    packagingReference,
    photoData?.correctPackaging,
    photoData?.customer,
    photoData?.customerOrderNoserv,
    photoData?.damagedPackaging,
    photoData?.description,
    photoData?.document9r1,
    photoData?.iberiaPackagingOut,
    photoData?.orderIn,
    photoData?.orderOut,
    photoData?.ownPackaging,
    photoData?.ownPackagingOut,
    photoData?.packagingReference,
    photoData?.sapWorkOrder,
    photoData?.serialIn,
    photoData?.serialOut,
    photoData?.step,
    photoData?.visible,
    sapWorkOrder,
    serialIn,
    serialOut,
    validation.isFormValid,
  ]);

  const openSaveModal = useCallback(
    async (
      event: React.BaseSyntheticEvent<
        Event,
        EventTarget & HTMLFormElement,
        EventTarget
      >,
    ) => {
      event.preventDefault();
      if (canNotSend) return;
      try {
        upLoader();
        if (allStepsChange) {
          const countResponse = await getAllStepsNumberPhotos(photoData.id);
          setConfirmCount(countResponse.count);
        } else if (orderChanged) {
          const orderCount = await getNumberPhotosInOrder(photoData.id);
          setConfirmCount(orderCount.count);
        } else if (groupChanged) {
          const groupCount = await getNumberPhotosInGroup(
            photoData.imageGroupId,
          );
          setConfirmCount(groupCount.count);
        }
        setOpenModal(true);
      } catch {
        appToast.error(literals.aProblem, literals.getNecesaryInfoError);
      } finally {
        downLoader();
      }
    },
    [
      upLoader,
      downLoader,
      photoData.imageGroupId,
      groupChanged,
      orderChanged,
      photoData.id,
      canNotSend,
      allStepsChange,
    ],
  );

  const saveChanges = useCallback(async () => {
    if (canNotSend) return;
    try {
      upLoader();
      if (groupChanged) {
        await changeGroupMetadata({
          imageId: photoData.imageId,
          imageGroupId: photoData.imageGroupId,
          visible: isVisible,
          description: description,
        });
      }

      if (orderChanged || allStepsChange) {
        const orderResponse = await updateOrder({
          orderId: photoData.id,
          imageId: photoData.imageId,

          customerOrderNoserv: customerOrderNoserv?.trim() || null,

          orderIn: orderIn?.trim() || null,
          orderOut: orderOut?.trim() || null,

          serialIn: serialIn?.trim() || null,
          serialOut: serialOut?.trim() || null,

          customer: customer?.trim() || null,
          sapWorkOrder: sapWorkOrder?.trim() || null,
          document9r1: document9r1?.trim() || null,

          packagingReference: packagingReference?.trim() || null,
          ownPackaging: fromYesNo(ownPackaging),
          correctPackaging: fromYesNo(correctPackaging),
          damagedPackaging: fromYesNo(damagedPackaging),
          ownPackagingOut: fromYesNo(ownPackagingOut),
          iberiaPackagingOut: fromYesNo(iberiaPackagingOut),
        });

        if (orderResponse?.warning || orderResponse?.restrict) {
          downLoader();
          setOpenModal(false);
          appToast.error(
            literals.aProblem,
            orderResponse?.message || literals.noPossibleSaveChanges,
          );
          return;
        }
      }

      downLoader();
      appToast.success(literals.changesOk, literals.complettedChanges);
      refetch();
      backToTable();
    } catch (error) {
      console.error(error);
      downLoader();
      setOpenModal(false);
      appToast.error(literals.aProblem, literals.noPossibleSaveChanges);
    }
  }, [
    upLoader,
    downLoader,

    photoData.imageGroupId,
    photoData.id,

    description,
    isVisible,

    customerOrderNoserv,
    orderIn,
    orderOut,
    serialIn,
    serialOut,
    customer,
    sapWorkOrder,
    document9r1,
    packagingReference,
    ownPackaging,
    correctPackaging,
    damagedPackaging,
    ownPackagingOut,
    iberiaPackagingOut,
    refetch,
    backToTable,
    photoData.imageId,
    canNotSend,
    groupChanged,
    orderChanged,
    allStepsChange,
  ]);

  const renderDescription = () => {
    if (photoData?.step === literals.inNoServ) {
      return (
        <>
          <div className="w-full mb-4">
            <InputSelect
              size="medium"
              label={literals.description}
              id="description"
              name="description"
              options={CAPTURE_DESCRIPTION_OPTIONS}
              onChange={changeDescriptionType}
              value={descriptionType}
              required
              placeholder={literals.chooseAnOption}
            />
          </div>
          <Textarea
            label={literals.extraInformation}
            onChange={changeExtraInformation}
            name="description"
            value={extraInformation}
            deleteMessage={literals.cleanExtrainfo}
            size="medium"
            required={description === "Others"}
          />
        </>
      );
    }
    if (isNacelleFindings) {
      return (
        <div className="md:max-w-lg w-full">
          <NacelleFinfingsInputSearchSelect
            label={literals.description}
            id={"description"}
            name="description"
            value={description}
            size={"medium"}
            onChange={(new_value: string) => changeOnltexDescription(new_value)}
          />
        </div>
      );
    }
    if (photoData?.step === literals.packagingAction) {
      return (
        <>
          <div className="w-full mb-4">
            <InputSelect
              size="medium"
              label={literals.description}
              id="description"
              name="description"
              options={PACKAGING_STEP_DESCRIPTION_OPTIONS}
              onChange={changeDescriptionType}
              value={descriptionType}
              placeholder={literals.chooseAnOption}
              required
            />
          </div>
          <Textarea
            label={literals.extraInformation}
            onChange={changeExtraInformation}
            name="description"
            value={extraInformation}
            deleteMessage={literals.cleanExtrainfo}
            size="medium"
          />
        </>
      );
    }
    return (
      <Textarea
        label={literals.description}
        onChange={changeOnltexDescription}
        name="description"
        value={description}
        deleteMessage={literals.cleanDescription}
        size="medium"
      />
    );
  };

  return (
    <>
      <form
        onSubmit={openSaveModal}
        className="flex flex-col w-full justify-between"
        data-testid="edit-image-form"
      >
        <div className="w-full my-4">
          {/* TOP META BLOCK */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            <InputText
              label={literals.date}
              placeholder={literals.date}
              value={formatDateDDMmmYYYY(photoData.createdAt) ?? ""}
              size="medium"
              onChange={() => {}}
              disabled
            />

            {/* Puesto (not editable) */}
            <InputSelect
              size="medium"
              label={literals.workstation}
              id="puesto"
              name="puesto"
              options={PUESTO_OPTIONS}
              value={photoData.area}
              onChange={() => {}}
              disabled
            />

            <InputSelect
              size="medium"
              label={literals.visibleClient}
              id="visibleClient"
              name="visibleClient"
              options={[
                { label: literals.yes, value: "YES" },
                { label: literals.no, value: "NO" },
              ]}
              value={isVisible ? "YES" : "NO"}
              onChange={(value) => setIsvisible(value === "YES")}
            />
          </div>
          {renderDescription()}

          <div className="mt-5 pt-5 border-t border-ib-pt-color-gray-300 grid grid-cols-1 md:grid-cols-2 gap-x-5">
            {/* PASO (not editable) */}
            <div className="mb-5">
              <InputSelect
                size="medium"
                label={literals.step}
                id="step"
                name="step"
                options={
                  photoData.area === literals.noServCell
                    ? PASO_NOSERV_OPTIONS
                    : photoData.area === literals.exits
                      ? PASO_EXITS_OPTIONS
                      : PASO_GARAGE_OPTIONS
                }
                value={photoData.step}
                onChange={() => {}}
                disabled
              />
            </div>

            <div className="mb-5">
              <InputText
                label={literals.customerOrderNoServ}
                placeholder={literals.customerOrderNoServ}
                value={customerOrderNoserv}
                size="medium"
                onChange={setCustomerOrderNoserv}
              />
            </div>

            <InputNumber
              label={literals.workOrder}
              placeholder={literals.workOrder}
              value={sapWorkOrder}
              size="medium"
              onChange={onChangeSapWorkOrder}
              helpText={literals.orderNumberFormat}
              error={sapWorkOrder !== "" && !/^\d{10,12}$/.test(sapWorkOrder)}
            />

            <div className="mb-5">
              <InputText
                label={literals.returnToOrigin}
                placeholder={literals.returnToOrigin}
                value={formatBooleanText(photoData.originReturn)}
                size="medium"
                onChange={setOrderIn}
                disabled
              />
            </div>

            <div className={photoData?.originReturn === true ? "mb-5" : ""}>
              <InputNumber
                label={literals.document9R1}
                placeholder={literals.document9R1}
                value={document9r1}
                size="medium"
                onChange={setDocument9r1}
                helpText={
                  photoData?.originReturn !== true
                    ? literals.r9Format
                    : undefined
                }
                error={
                  (document9r1 !== "" && !/^\d{10}$/.test(document9r1)) ||
                  canNotDelete9r1
                }
                disabled={photoData?.originReturn === true}
                required={photoData?.document9r1?.length > 0}
              />
            </div>

            <div className="mb-5">
              <InputText
                label={literals.partIn}
                placeholder={literals.partIn}
                value={orderIn}
                size="medium"
                onChange={setOrderIn}
              />
            </div>

            <div className="mb-5">
              <InputText
                label={literals.serialIn}
                placeholder={literals.serialIn}
                value={serialIn}
                size="medium"
                onChange={setSerialIn}
              />
            </div>

            <div className="mb-5">
              <InputText
                label={literals.partOut}
                placeholder={literals.partOut}
                value={orderOut}
                size="medium"
                onChange={setOrderOut}
              />
            </div>

            <div className="mb-5">
              <InputText
                label={literals.serialOut}
                placeholder={literals.serialOut}
                value={serialOut}
                size="medium"
                onChange={setSerialOut}
              />
            </div>

            <InputNumber
              label={literals.client}
              placeholder={literals.client}
              value={customer}
              size="medium"
              onChange={onChangeCustomer}
              helpText={literals.customerFormat}
              error={customer !== "" && !/^\d{3,6}$/.test(customer)}
            />

            <div className="mb-5">
              <InputSelect
                size="medium"
                label={literals.ownPackaging}
                id="ownPackaging"
                name="ownPackaging"
                options={YES_NO_OPTIONS}
                value={ownPackaging}
                onChange={onChangeOwnPackaging}
                placeholder={literals.selectOption}
              />
            </div>

            <div className="mb-5">
              <InputSelect
                size="medium"
                label={literals.correctPackaging}
                id="correctPackaging"
                name="correctPackaging"
                options={YES_NO_OPTIONS}
                value={correctPackaging}
                onChange={setCorrectPackaging}
                placeholder={literals.selectOption}
              />
            </div>

            <div className="mb-5">
              <InputText
                label={literals.packagingRef}
                placeholder={literals.packagingRef}
                value={packagingReference}
                size="medium"
                onChange={() => {}}
                disabled
              />
            </div>

            <div className="mb-5">
              <InputSelect
                size="medium"
                label={literals.damagedPackaging}
                id="damagedPackaging"
                name="damagedPackaging"
                options={YES_NO_OPTIONS}
                value={damagedPackaging}
                onChange={setDamagedPackaging}
                placeholder={literals.selectOption}
              />
            </div>

            <InputSelect
              size="medium"
              label={literals.ownPackagingOut}
              id="ownPackagingOut"
              name="ownPackagingOut"
              options={YES_NO_OPTIONS}
              value={ownPackagingOut}
              onChange={setOwnPackagingOut}
              placeholder={literals.selectOption}
            />

            <InputSelect
              size="medium"
              label={literals.iberiaPackagingOut}
              id="iberiaPackagingOut"
              name="iberiaPackagingOut"
              options={YES_NO_OPTIONS}
              value={iberiaPackagingOut}
              onChange={setIberiaPackagingOut}
              placeholder={literals.selectOption}
            />
          </div>
        </div>

        <div className="py-4 flex flex-col gap-4 border-t border-ib-pt-color-gray-300">
          <PrimaryButton
            size="medium"
            label={literals.saveChanges}
            colour="red"
            type="submit"
            disabled={canNotSend}
          />
          <SecondaryButton
            size="medium"
            label={literals.cancel}
            colour="red"
            onClick={closeForm}
          />
        </div>
      </form>

      <ConfirmEditionDetails
        saveChanges={saveChanges}
        opened={openModal}
        onClose={() => setOpenModal(false)}
        totalImagesAffected={confirmCount}
      />
    </>
  );
};

export default EditionPhotoDetails;
